import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash, FaFlag } from '../../components/Icons';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';
import { CarreraServiceFirestore } from '../../services/CarreraServiceFirestore';
import { theme } from '../../theme';

const Container = styled.div`
  padding: 24px;
  max-width: 1280px;
  margin: 0 auto;
  background-color: ${theme.colors.background.default};
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-weight: ${theme.typography.fontWeights.bold};
  font-size: 2.2rem;
  position: relative;
  padding-bottom: 12px;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background-color: ${theme.colors.secondary.main};
    border-radius: 2px;
  }
  
  svg {
    margin-right: 16px;
    color: ${theme.colors.secondary.main};
  }
`;

const Button = styled.button`
  background-color: ${theme.colors.secondary.main};
  color: ${theme.colors.secondary.contrastText};
  border: none;
  border-radius: ${theme.borderRadius.small};
  padding: 12px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  box-shadow: ${theme.shadows.small};
  
  &:hover {
    background-color: ${theme.colors.secondary.dark};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.medium};
  }
  
  svg {
    margin-right: 10px;
    font-size: 1.1rem;
  }
`;

const TableContainer = styled.div`
  overflow: hidden;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  background-color: ${theme.colors.background.paper};
  margin-bottom: 24px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  
  th, td {
    padding: 16px;
    text-align: left;
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.contrastText};
    font-weight: ${theme.typography.fontWeights.semibold};
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  th:first-child {
    border-top-left-radius: ${theme.borderRadius.small};
  }
  
  th:last-child {
    border-top-right-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:first-child {
    border-bottom-left-radius: ${theme.borderRadius.small};
  }
  
  tr:last-child td:last-child {
    border-bottom-right-radius: ${theme.borderRadius.small};
  }
  
  tr:nth-child(even) td {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  td {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    color: ${theme.colors.text.primary};
  }
  
  tr:hover td {
    background-color: rgba(228, 49, 46, 0.05);
  }
`;

const Th = styled.th`
  background-color: ${theme.colors.primary.main};
  color: ${theme.colors.primary.contrastText};
  font-weight: ${theme.typography.fontWeights.semibold};
  padding: 16px;
  text-align: left;
`;

const Td = styled.td`
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  color: ${theme.colors.text.primary};
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  margin-right: 12px;
  color: ${theme.colors.primary.main};
  font-size: 1.1rem;
  padding: 8px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${theme.colors.secondary.main};
    background-color: rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 32px;
  background-color: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  color: ${theme.colors.text.secondary};
  font-size: 1.1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.secondary.main};
    opacity: 0.7;
  }
`;

// Filtros
const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 24px;
  background-color: ${theme.colors.background.paper};
  padding: 24px;
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.medium};
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-weight: ${theme.typography.fontWeights.semibold};
  margin-bottom: 8px;
  color: ${theme.colors.text.primary};
  font-size: 0.9rem;
`;

const FilterSelect = styled.select`
  padding: 10px 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.small};
  font-size: 0.95rem;
  transition: all 0.2s ease;
  background-color: ${theme.colors.background.default};
  color: ${theme.colors.text.primary};
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 0 0 2px rgba(16, 19, 37, 0.1);
  }
`;

// Modal de confirmación
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  color: #1a1a2e;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const CancelButton = styled(Button)`
  background-color: #6c757d;
  
  &:hover {
    background-color: #5a6268;
  }
`;

const DeleteButton = styled(Button)`
  background-color: #dc3545;
  
  &:hover {
    background-color: #c82333;
  }
`;

const CarrerasList: React.FC = () => {
  const { data, loadData } = useContext(AppContext);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [carreraToDelete, setCarreraToDelete] = useState<string | null>(null);
  const [ligaFilter, setLigaFilter] = useState<string>('');
  
  // Filtro rápido por liga
  const ligas = data.ligas || [];
  const carrerasFiltradas = ligaFilter ? data.carreras.filter(c => c.ligaId === ligaFilter) : data.carreras;

  // Función para manejar la eliminación de una carrera
  const handleDelete = (carreraId: string) => {
    setCarreraToDelete(carreraId);
    setShowDeleteModal(true);
  };
  
  // Función para cambiar el estado de publicación de una carrera
  const togglePublicada = async (carreraId: string, publicada: boolean) => {
    try {
      await CarreraServiceFirestore.togglePublicada(carreraId, !publicada);
      await loadData(); // Recargar los datos después de actualizar
    } catch (error) {
      console.error('Error al cambiar el estado de publicación:', error);
      alert('Error al cambiar el estado de publicación de la carrera');
    }
  };
  
  // Función para confirmar la eliminación
  const confirmDelete = async () => {
    if (carreraToDelete) {
      await CarreraServiceFirestore.delete(carreraToDelete);
      await loadData(); // Recargar los datos después de eliminar
      setShowDeleteModal(false);
      setCarreraToDelete(null);
    }
  };
  
  // Función para cancelar la eliminación
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setCarreraToDelete(null);
  };
  
  // Filtrar carreras por liga
  const filteredCarreras = ligaFilter 
    ? data.carreras.filter(carrera => carrera.ligaId === ligaFilter)
    : data.carreras;
  
  // Ordenar carreras por liga y nombre
  const sortedCarreras = [...filteredCarreras].sort((a, b) => {
    // Primero ordenar por liga
    const ligaA = data.ligas.find(l => l.id === a.ligaId)?.nombre || '';
    const ligaB = data.ligas.find(l => l.id === b.ligaId)?.nombre || '';
    
    if (ligaA !== ligaB) {
      return ligaA.localeCompare(ligaB);
    }
    
    // Luego ordenar por nombre de carrera
    return a.nombre.localeCompare(b.nombre);
  });
  return (
    <Container>
      <SectionHeaderComp icon={<FaFlag style={{ color: '#e4312e' }} />}>
        Gestión de Carreras
      </SectionHeaderComp>
      
      <Link to="/carreras/new">
        <Button>
          <FaPlus /> Nueva Carrera
        </Button>
      </Link>
      
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="ligaFilter">Filtrar por Liga:</FilterLabel>
          <FilterSelect 
            id="ligaFilter" 
            value={ligaFilter} 
            onChange={(e) => setLigaFilter(e.target.value)}
          >
            <option value="">Todas las ligas</option>
            {data.ligas.map(liga => (
              <option key={liga.id} value={liga.id}>
                {liga.nombre}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
      </FiltersContainer>
      
      {sortedCarreras.length > 0 ? (
        <TableContainer>
        <Table>
          <thead>
            <tr>
              <Th>Nombre</Th>
              <Th>Liga</Th>
              <Th>Circuito</Th>
              <Th>Fecha</Th>
              <Th>Participantes</Th>
              <Th>Publicada</Th>
              <Th>Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {sortedCarreras.map(carrera => {
              const liga = data.ligas.find(l => l.id === carrera.ligaId);
              const participantes = carrera.resultados.length;
              const sociosActuales = data.socios.filter(s => s.ligaAsignada === carrera.ligaId).length;
              const participantesNoCoinciden = participantes !== sociosActuales;
              return (
                <tr key={carrera.id}>
                  <Td>{carrera.nombre}</Td>
                  <Td>{liga ? liga.nombre : '-'}</Td>
                  <Td>{carrera.circuito}</Td>
                  <Td>{carrera.fecha}</Td>
                  <Td style={{ color: participantesNoCoinciden ? 'red' : undefined }}>
                    {participantes}
                    {participantesNoCoinciden && <span title="El número de participantes no coincide con los socios actuales de la liga" style={{marginLeft: 5}}>⚠️</span>}
                  </Td>
                  <Td>
                    <ActionButton 
                      onClick={() => togglePublicada(carrera.id, carrera.publicada || false)}
                      title={carrera.publicada ? 'Ocultar de la clasificación' : 'Mostrar en la clasificación'}
                      style={{ color: carrera.publicada ? '#28a745' : '#dc3545' }}
                    >
                      {carrera.publicada ? <FaEye /> : <FaEyeSlash />}
                    </ActionButton>
                    <span style={{ marginLeft: '5px' }}>
                      {carrera.publicada ? 'Sí' : 'No'}
                    </span>
                  </Td>
                  <Td>
                    <Link to={`/carreras/${carrera.id}`}>
                      <ActionButton>
                        <FaEdit />
                      </ActionButton>
                    </Link>
                    <ActionButton onClick={() => handleDelete(carrera.id)}>
                      <FaTrash />
                    </ActionButton>
                  </Td>
                </tr>
              );
            })}
          </tbody>
          </Table>
      </TableContainer>
      ) : (
        <EmptyMessage>
          <FaFlag />
          <span>No hay carreras disponibles</span>
        </EmptyMessage>
      )}
      
      {/* Modal de confirmación para eliminar */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Confirmar eliminación</ModalTitle>
            <p>¿Estás seguro de que deseas eliminar esta carrera? Esta acción no se puede deshacer.</p>
            <p>Se eliminarán también todos los resultados y sanciones asociados a esta carrera.</p>
            <ModalButtons>
              <CancelButton onClick={cancelDelete}>Cancelar</CancelButton>
              <DeleteButton onClick={confirmDelete}>Eliminar</DeleteButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default CarrerasList;
